import { useAddTransaction } from '../services/hooks/transaction/transaction';
import { useGetProduct } from '../services/hooks/product/product';
import { FaCheckCircle } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { validateEmail } from './FormValidation';
import { FormError } from './FormError';
import FloatingMessage from './FloatingMessage';

const subscriptionFeatures = [
  'Semua Akun Online Terjaga – Rekening & Kartu, Gmail, Media Sosial, dll. ',
  'Notifikasi Kebocoran Data Real-Time ',
  'Panduan Keamanan yang Jelas & Mudah di Satu Portal ',
];

export default function SubscriptionPackage(state: { isActive: boolean }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [emailError, setEmailError] = useState('');
  const [email, setEmail] = useState('');
  const [showModal, setShowModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data = [], isLoading: isProductLoading } = useGetProduct({
    params: {},
    enabled: true,
    onError(error) {
      const axiosErr = error as any;
      if (axiosErr.response?.status === 401) {
        navigate(`/404`, { replace: true });
      } else {
        setErrorMessage('Terjadi Kesalahan. Silakan coba lagi.');
      }
    },
  });

  const { mutate: addTransaction, isLoading } = useAddTransaction({
    onSuccess: (res, _) => {
      window.location.href = res.data.payment_link;
    },
    onError: (error) => {
      setErrorMessage('Gagal Membuat Transaksi.');
    },
  });

  const validateForm = () => {
    const emailValidation = validateEmail(email);
    setEmailError(emailValidation.message);
    return emailValidation.isValid;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    setShowModal(true);
  };

  const handleAgree = () => {
    setShowModal(false);
    addTransaction({
      body: {
        email: email,
      },
    });
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  return (
    <div className="bg-gradient-to-br from-pink-50 via-white to-gray-100 border-2 border-[#3723B5]/10 p-4 rounded-2xl shadow-lg mb-8 group animate-fade-in-down">
      {errorMessage && (
        <FloatingMessage
          text={errorMessage}
          type="error"
          show={true}
          duration={3000}
        />
      )}
      <div className="mb-5">
        <h3 className="text-2xl font-extrabold mb-1 drop-shadow-sm text-center">
          <span className="text-[#3723B5]">SMART</span>SECURE
        </h3>
        <p className="text-base text-gray-700 animate-fade-in">
          Perlindungan menyeluruh hingga 10 Email
        </p>
      </div>

      <div className="space-y-3 mb-8">
        {subscriptionFeatures.map((feature, index) => (
          <div
            key={index}
            className="flex items-center space-x-3 animate-fade-in"
            style={{
              animationDelay: `${0.05 * index + 0.2}s`,
              animationFillMode: 'both',
            }}
          >
            <FaCheckCircle className="text-[#00D100] shrink-0 drop-shadow-sm" />
            <span className="text-base text-gray-800">{feature}</span>
          </div>
        ))}
      </div>

      <div className=" text-center space-y-1 mb-6">
        <span className="text-sm text-gray-500">Mulai dari</span>
        <div className="text-2xl font-bold text-gray-900 animate-fade-in">
          {data?.data?.[0]?.meta?.price.toLocaleString('id-ID')}
          <span className="text-base font-normal">/bulan</span>
          <div className="text-xs text-gray-500 font-normal">
            Harga minimal, proteksi maksimal
          </div>
        </div>
      </div>

      <input
        hidden={state.isActive && pathname !== '/subscribe'}
        type="email"
        placeholder="Masukkan alamat email"
        onChange={(e) => {
          setEmail(e.target.value);
          setEmailError('');
        }}
        className={`w-full px-3 py-2 text-sm border ${
          emailError ? 'border-red-500' : 'border-gray-300'
        } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3723B5]`}
      />

      <FormError message={emailError} />

      <button
        hidden={state.isActive && pathname !== '/subscribe'}
        onClick={handleSubmit}
        className="text-sm w-full py-3 bg-gradient-to-r from-[#3723B5] to-[#3723B5] text-white font-bold rounded-xl shadow-md text-lg animate-fade-in-up mt-4"
        style={{ animationDelay: '0.7s', animationFillMode: 'both' }}
      >
        {data?.data?.[0]?.meta?.free_trial
          ? 'Bayar 1 Bulan, Nikmati 2 Bulan Pertama'
          : 'Aktifkan Sekarang'}
        <h6
          className="text-xs font-light text-white text-center"
          style={{ animationDelay: '0.8s', animationFillMode: 'both' }}
        >
          Tanpa Kartu Kredit. Batalkan kapan saja
        </h6>
      </button>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full mx-4">
            <div className="text-center mb-6">
              <h2 className="text-lg font-bold mb-3 text-[#3723B5]">
                Konfirmasi Aktivasi
              </h2>
              <p className="text-gray-700">
                Apakah anda ingin lanjutkan aktivasi?
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="flex-1 py-3 rounded-lg font-bold border-2 border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Tidak
              </button>
              <button
                onClick={handleAgree}
                disabled={isLoading}
                className={`flex-1 py-3 rounded-lg font-bold transition-colors ${
                  isLoading
                    ? 'bg-gray-300 text-gray-400 cursor-not-allowed'
                    : 'bg-[#3723B5] text-white hover:bg-[#2d1a94]'
                }`}
              >
                {isLoading ? 'Memproses...' : 'Ya'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
